import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartRisk } from "@/shared/types/chart-risk";

import { LINE_CHART_MARGIN_PARAMS_PX } from "../constants";
import { ChartWrapper } from "../styled/chart-wrapper";
import { numberToPercent } from "../utils/number-to-percent";

const formatDayParam = (day: number) => `${day} ${day === 1 ? "day" : "days"}`;

enum Volatility {
  Maximum = "maximum",
  Minimum = "minimum",
  Percentile25 = "percentile25",
  Percentile50 = "percentile50",
  Percentile75 = "percentile75",
}

const labelByVolatilityType: Record<Volatility, string> = {
  [Volatility.Maximum]: "maximum",
  [Volatility.Minimum]: "minimum",
  [Volatility.Percentile25]: "25% quartile",
  [Volatility.Percentile50]: "median",
  [Volatility.Percentile75]: "75% quartile",
};

interface Props {
  data?: ChartRisk["volatilityCones"];
}

export const VolatilityConesChart: React.FC<Props> = ({ data }) => {
  return (
    <ChartWrapper>
      <ResponsiveContainer>
        <LineChart data={data} margin={LINE_CHART_MARGIN_PARAMS_PX}>
          <YAxis tickFormatter={numberToPercent} />
          <XAxis dataKey="period" tickFormatter={formatDayParam} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            labelFormatter={formatDayParam}
            formatter={(value, name: Volatility) => {
              return [
                typeof value === "number" && numberToPercent(value),
                labelByVolatilityType[name],
              ];
            }}
          />
          <Line
            type="monotone"
            dataKey={Volatility.Maximum}
            stroke="#1f77b4"
            dot={<span />}
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey={Volatility.Percentile75}
            stroke="#ff7f0f"
            dot={<span />}
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey={Volatility.Percentile50}
            stroke="#2aa02b"
            dot={<span />}
          />
          <Line
            type="monotone"
            dataKey={Volatility.Percentile25}
            stroke="#d62727"
            dot={<span />}
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey={Volatility.Minimum}
            stroke="#9367bd"
            dot={<span />}
            strokeDasharray="5 5"
          />
          <Legend
            formatter={(value: Volatility) => labelByVolatilityType[value]}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};
